import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

import {
  deleteSnippet,
  encodeText,
  fetchSnippets,
  LINE_ENDINGS,
  parseHex,
  remoteErrorText,
  saveSnippet,
  TerminalClient,
  terminalUrl,
  toAscii,
  toHex,
  transcriptText,
  type LineEnding,
  type RemoteSession,
  type Snippet,
  type TerminalState,
} from './remoteApi';

/** Lines kept on screen (the full transcript is recorded server side). */
const MAX_LINES = 2000;

interface Line {
  ts: Date;
  direction: 'up' | 'down';
  bytes: Uint8Array;
}

export interface RemoteTerminalProps {
  apiUrl: string;
  tenantId: string;
  /** The API access token (snippets). */
  token: string;
  session: RemoteSession;
  /** The session's terminal token, from opening it. */
  sessionToken: string;
  /** The target's driver: its snippets are offered first. */
  driver: string | null;
  onEnd: () => Promise<void>;
  onClose: () => void;
  socketFactory?: (url: string) => WebSocket;
}

/**
 * A TCP terminal to an access target (RAX-003): what is sent and received,
 * as ASCII or hex, with timestamps; a line ending chosen per send; text or
 * hex input; saved snippets; and the transcript as a download.
 */
export function RemoteTerminal({
  apiUrl,
  tenantId,
  token,
  session,
  sessionToken,
  driver,
  onEnd,
  onClose,
  socketFactory,
}: RemoteTerminalProps) {
  const [lines, setLines] = useState<Line[]>([]);
  const [state, setState] = useState<{ s: TerminalState; reason?: string }>({ s: 'connecting' });
  const [hexView, setHexView] = useState(false);
  const [showTime, setShowTime] = useState(true);
  const [ending, setEnding] = useState<LineEnding>('crlf');
  const [hexInput, setHexInput] = useState(false);
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const client = useRef<TerminalClient | null>(null);
  const output = useRef<HTMLDivElement | null>(null);

  const push = (direction: 'up' | 'down', bytes: Uint8Array) => {
    setLines((prev) => [...prev, { ts: new Date(), direction, bytes }].slice(-MAX_LINES));
  };

  useEffect(() => {
    const c = new TerminalClient({
      url: terminalUrl(apiUrl),
      sessionId: session.id,
      token: sessionToken,
      onData: (bytes) => {
        push('down', bytes);
      },
      onState: (s, reason) => {
        setState({ s, reason });
      },
      socketFactory,
    });
    client.current = c;
    return () => {
      client.current = null;
      c.close();
    };
  }, [apiUrl, session.id, sessionToken, socketFactory, attempt]);

  useEffect(() => {
    const controller = new AbortController();
    fetchSnippets(apiUrl, token, tenantId, controller.signal)
      .then(setSnippets)
      .catch(() => {
        // Snippets are a convenience; the terminal works without them.
      });
    return () => {
      controller.abort();
    };
  }, [apiUrl, token, tenantId]);

  useEffect(() => {
    const el = output.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const bytesOf = (text: string, hex: boolean): Uint8Array<ArrayBuffer> | null =>
    hex ? parseHex(text) : encodeText(text, ending);

  const send = (text: string, hex: boolean) => {
    const bytes = bytesOf(text, hex);
    if (!bytes) {
      setInputError('Enter hex byte pairs, e.g. 0d 0a');
      return false;
    }
    if (bytes.length === 0) return false;
    client.current?.send(bytes);
    push('up', bytes);
    setInputError(null);
    return true;
  };

  const ready = state.s === 'ready';
  const ordered = [...snippets].sort(
    (a, b) =>
      Number(b.driver === driver) - Number(a.driver === driver) || a.name.localeCompare(b.name),
  );
  const visible = ordered.filter((s) => s.driver === null || s.driver === driver);

  const download = () => {
    const text = transcriptText(lines.map((l) => ({ ...l, ts: l.ts.toISOString() })));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    a.download = `terminal-${session.id}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Stack spacing={1.5} data-testid="terminal">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            {session.target?.name ?? 'Terminal'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {session.target ? `${session.target.host}:${String(session.target.port)}` : ''}
          </Typography>
          <Chip
            size="small"
            color={ready ? 'success' : state.s === 'closed' ? 'default' : 'info'}
            label={ready ? 'Connected' : state.s === 'closed' ? 'Disconnected' : 'Connecting'}
            data-testid="terminal-state"
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {state.s === 'closed' && (
            <Button
              size="small"
              onClick={() => {
                setAttempt((n) => n + 1);
              }}
              data-testid="terminal-reconnect"
            >
              Reconnect
            </Button>
          )}
          <Button size="small" onClick={download} disabled={lines.length === 0}>
            Download
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => {
              onEnd()
                .then(onClose)
                .catch((err: unknown) => {
                  setError(remoteErrorText(err));
                });
            }}
            data-testid="terminal-end"
          >
            End session
          </Button>
        </Stack>
      </Stack>
      {state.s === 'closed' && state.reason && (
        <Alert severity="info" data-testid="terminal-closed">
          {state.reason}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={hexView ? 'hex' : 'ascii'}
          onChange={(_, v: string | null) => {
            if (v) setHexView(v === 'hex');
          }}
          aria-label="Show bytes as"
        >
          <ToggleButton value="ascii" data-testid="view-ascii">
            ASCII
          </ToggleButton>
          <ToggleButton value="hex" data-testid="view-hex">
            Hex
          </ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showTime}
              onChange={(e) => {
                setShowTime(e.target.checked);
              }}
            />
          }
          label="Timestamps"
        />
      </Stack>

      <Box
        ref={output}
        role="log"
        aria-label="Terminal output"
        data-testid="terminal-output"
        sx={{
          height: { xs: '40vh', sm: 320 },
          overflow: 'auto',
          p: 1,
          bgcolor: 'grey.900',
          color: 'grey.100',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {lines.length === 0 && (
          <Typography
            component="span"
            sx={{ color: 'grey.500', fontFamily: 'inherit', fontSize: 'inherit' }}
          >
            {ready ? 'Connected. Type a command below.' : 'Connecting through the appliance…'}
          </Typography>
        )}
        {lines.map((l, i) => (
          <div key={i} data-direction={l.direction}>
            {showTime && (
              <Box component="span" sx={{ color: 'grey.500' }}>
                {l.ts.toLocaleTimeString()}{' '}
              </Box>
            )}
            <Box
              component="span"
              sx={{ color: l.direction === 'up' ? 'warning.light' : 'success.light' }}
            >
              {l.direction === 'up' ? '> ' : '< '}
            </Box>
            {hexView ? toHex(l.bytes) : toAscii(l.bytes)}
          </div>
        ))}
      </Box>

      <Stack
        component="form"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        onSubmit={(e) => {
          e.preventDefault();
          if (send(input, hexInput)) setInput('');
        }}
      >
        <TextField
          size="small"
          fullWidth
          label={hexInput ? 'Hex bytes' : 'Command'}
          value={input}
          error={inputError !== null}
          helperText={inputError ?? undefined}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          disabled={!ready}
          slotProps={{
            htmlInput: {
              'data-testid': 'terminal-input',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false,
              style: { fontFamily: 'monospace' },
            },
          }}
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          <TextField
            select
            size="small"
            label="Ends with"
            value={ending}
            disabled={hexInput}
            onChange={(e) => {
              setEnding(e.target.value as LineEnding);
            }}
            sx={{ minWidth: 110 }}
            slotProps={{ htmlInput: { 'data-testid': 'terminal-ending' } }}
          >
            {(Object.keys(LINE_ENDINGS) as LineEnding[]).map((k) => (
              <MenuItem key={k} value={k}>
                {LINE_ENDINGS[k].label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hexInput}
                onChange={(e) => {
                  setHexInput(e.target.checked);
                  setInputError(null);
                }}
              />
            }
            label="Hex"
          />
          <Button type="submit" variant="contained" disabled={!ready} data-testid="terminal-send">
            Send
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Snippets:
        </Typography>
        {visible.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            none yet
          </Typography>
        )}
        {visible.map((s) => (
          <Chip
            key={s.id}
            label={s.name}
            title={s.body}
            disabled={!ready}
            onClick={() => {
              send(s.body, s.encoding === 'hex');
            }}
            onDelete={() => {
              deleteSnippet(apiUrl, token, tenantId, s.id)
                .then(() => {
                  setSnippets((prev) => prev.filter((x) => x.id !== s.id));
                })
                .catch((err: unknown) => {
                  setError(remoteErrorText(err));
                });
            }}
            data-testid={`snippet-${s.name}`}
          />
        ))}
        <Button
          size="small"
          disabled={input.trim() === ''}
          onClick={() => {
            const name = window.prompt('Name this snippet', input.slice(0, 40));
            if (!name) return;
            saveSnippet(apiUrl, token, tenantId, {
              name,
              body: input,
              encoding: hexInput ? 'hex' : 'text',
              driver,
            })
              .then((s) => {
                setSnippets((prev) => [...prev, s]);
              })
              .catch((err: unknown) => {
                setError(remoteErrorText(err));
              });
          }}
          data-testid="save-snippet"
        >
          Save input as snippet
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Everything sent and received is recorded in the session&apos;s transcript.
      </Typography>
    </Stack>
  );
}
